import { useMemo, useRef } from 'react';
import { useJnx } from '../../../util/jnx';
import { useSideChannelSubscription } from '../../../util/useSideChannel';
import getPathFromId from '../../../util/getPathFromId';

// Shared "select all / deselect all" logic for a boolean array-item column
// (`ui:includeToggleAll: true` on the item property, e.g. a CheckboxField).
// Used by both TableLayout (button in the column header) and CardsLayout
// (button in a small toolbar above the cards, since cards have no header
// row/column to attach to).
//
// Skips rows gated by `ui:readonlyRowIf`, mirroring how each row's own
// checkbox is disabled. After the bulk write, re-evaluates the field's
// `ui:onCheck`/`ui:onUncheck` ONCE (not once per row) — the same JNX a
// single manual click would trigger — so derived state that depends on
// "which rows are checked" (see CheckboxField) stays in sync with a bulk
// toggle instead of only reacting to individual clicks.
function useToggleAll({ field, formData, registry, fieldPathId, uiSchema }) {
  const formContext = registry?.formContext;
  const { setFormDataValues, sideChannel } = formContext || {};
  const rootFormData = useSideChannelSubscription(sideChannel, 0) || {};
  // Delayed onCheck/onUncheck eval (below) reads this AFTER the bulk write has
  // propagated — a ref updated fresh every render (not memoized) so the stale
  // closure from click-time sees the post-toggle root data, same trick
  // CheckboxField uses for its own onCheck/onUncheck.
  const rootFormDataRef = useRef(rootFormData);
  rootFormDataRef.current = rootFormData;

  const itemsUiSchema = uiSchema?.items || {};
  const { 'ui:readonlyRowIf': readonlyRowIf } = itemsUiSchema;
  const { 'ui:onCheck': onCheckExpr, 'ui:onUncheck': onUncheckExpr } = itemsUiSchema[field] || {};

  const functionBinds = useMemo(() => ({ set: setFormDataValues }), [setFormDataValues]);
  const readonlyRowIfJnx = useJnx(readonlyRowIf);
  const onCheckJnx = useJnx(onCheckExpr, { functionBinds });
  const onUncheckJnx = useJnx(onUncheckExpr, { functionBinds });

  const arrayPath = useMemo(() => getPathFromId(fieldPathId?.$id), [fieldPathId?.$id]);
  const rows = formData || [];

  const eligibleIdxs = useMemo(
    () =>
      rows.reduce((acc, item, idx) => {
        const isReadOnly = !!(readonlyRowIfJnx && readonlyRowIfJnx.eval(item || {}, '', { root: rootFormData, formContext }));
        if (!isReadOnly) acc.push(idx);

        return acc;
      }, []),
    [rows, readonlyRowIfJnx, rootFormData, formContext]
  );

  const checkedCount = eligibleIdxs.filter(idx => !!rows[idx]?.[field]).length;
  const allChecked = eligibleIdxs.length > 0 && checkedCount === eligibleIdxs.length;
  const someChecked = checkedCount > 0 && !allChecked;

  const toggleAll = e => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!eligibleIdxs.length || !setFormDataValues) return;

    const nextValue = !allChecked;
    const newValues = eligibleIdxs.reduce((acc, idx) => {
      acc[`${arrayPath}.${idx}.${field}`] = nextValue;

      return acc;
    }, {});

    setFormDataValues(newValues);

    const changeJnx = nextValue ? onCheckJnx : onUncheckJnx;

    if (changeJnx) {
      setTimeout(() => {
        changeJnx.eval(rootFormDataRef.current, arrayPath, {
          rootData: rootFormDataRef.current,
          fieldPath: arrayPath,
        });
      }, 300);
    }
  };

  return { allChecked, someChecked, toggleAll, disabled: eligibleIdxs.length === 0 };
}

export default useToggleAll;
